import type { MarkdownImageEditRequest } from './markdown-image-node';

import {
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import { CenteredDialogContent } from '#components/centered-dialog-content/centered-dialog-content';
import { toaster } from '#components/toaster/toaster';

export interface MarkdownImageEditModalProps {
  request: MarkdownImageEditRequest | null;
  onDismiss: () => void;
}

function normalizeEditableValue(value: string): string {
  return value.trim();
}

export function MarkdownImageEditModal({
  request,
  onDismiss,
}: MarkdownImageEditModalProps) {
  const open = request != null;
  const srcRef = useRef<HTMLInputElement>(null);
  const [src, setSrc] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!request) {
      setSaving(false);
      return;
    }

    setSrc(request.initialValues.src);
    setWidth(request.initialValues.width);
    setHeight(request.initialValues.height);
    queueMicrotask(() => srcRef.current?.focus());
  }, [request]);

  const handleClose = () => {
    if (saving) return;

    onDismiss();
  };

  const handleSave = async () => {
    if (!request) return;

    const nextSrc = normalizeEditableValue(src);
    if (!nextSrc) {
      toaster.create({
        title: 'Missing image URL',
        description: 'Enter a source URL before saving the image.',
        type: 'warning',
      });
      return;
    }

    setSaving(true);
    try {
      await request.onCommit({
        src: nextSrc,
        width: normalizeEditableValue(width),
        height: normalizeEditableValue(height),
      });
      onDismiss();
    } catch (error) {
      toaster.create({
        title: 'Could not save image',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      size="lg"
    >
      <CenteredDialogContent maxW="min(90vw, 32rem)">
        <DialogHeader>
          <DialogTitle>
            {request?.mode === 'insert' ? 'Insert image' : 'Edit image'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            <Text fontSize="sm" color="fg.muted">
              Set the image source and optional width and height attributes.
            </Text>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                Source URL
              </Text>
              <Input
                ref={srcRef}
                type="text"
                inputMode="url"
                placeholder="https://…"
                value={src}
                onChange={(e) => setSrc(e.target.value)}
                disabled={saving}
                autoComplete="off"
              />
            </label>
            <HStack gap={3} align="start" flexWrap="wrap">
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  flex: '1 1 9rem',
                }}
              >
                <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                  Width
                </Text>
                <Input
                  type="number"
                  min={1}
                  placeholder="e.g. 640"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  disabled={saving}
                  autoComplete="off"
                />
              </label>
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  flex: '1 1 9rem',
                }}
              >
                <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                  Height
                </Text>
                <Input
                  type="number"
                  min={1}
                  placeholder="e.g. 480"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  disabled={saving}
                  autoComplete="off"
                />
              </label>
            </HStack>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <HStack gap={2} flexWrap="wrap">
            <DialogActionTrigger asChild>
              <Button variant="outline" onClick={handleClose} disabled={saving}>
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button colorPalette="blue" onClick={handleSave} disabled={saving}>
              Save image
            </Button>
          </HStack>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
